import { FormattedMessage, useIntl } from 'react-intl';
import { Box, ClosableDrawer } from '@atoms/layout';
import { Chip, Grid, Stack, styled, Typography, useTheme } from '@mui/material';
import { Button } from '@atoms/buttons';
import { useNotification } from '@libs/snackbar';
import { set, useForm, useFormContext } from 'react-hook-form';
import {
  CollapsibleFormBlock,
  Form,
  FormBlock,
  FormCheckbox,
  FormSelect,
  FormTextField,
  FormTimePicker,
} from '@molecules/form';

import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useShiftTemplateDrawerStore } from '@libs/store/shiftTemplates';
import { useOrgQualificationsStore } from '@libs/store/settings';
import { useShiftTypePresetsList } from '@libs/store/shiftTypePresets';
import {
  DayScheduleSection,
  TimeRangeRecord,
  WeekVariants,
  RotationSequenceSection,
} from '@pages/schedule/drawers';
import { Schedule } from '@libs/models/shifts/series';
import { WeekDay } from '@libs/models/common';
import { useLocationStore } from '@libs/store/locations';
import { SequenceItem } from '@libs/models/shifts/rotation';
import { SingleShiftTemplateCreateData } from '@libs/api/shiftTemplates';
import { parseError } from '@libs/api/errors';
import { QualificationSelect } from '@pages/schedule/drawers/components';
import { LocationFormData, LocationSelectionSection } from '@pages/schedule/components';

type DrawerProps = {
  onSaveCall?: () => void;
};
export function EditShiftTemplateDrawer({ onSaveCall }: DrawerProps) {
  const intl = useIntl();
  const theme = useTheme();
  const { showError, showErrorIntl } = useNotification();
  const { loading: locationsLoading, current: currentLocation, currentLocationID } = useLocationStore();

  const {
    open,
    drawerMode,
    loading: entityLoading,
    currentEntityId,
    entity,
    closeDrawer,
    create,
    update,
    delete: deleteEntity,
  } = useShiftTemplateDrawerStore();
  const { load: loadOrgQuals } = useOrgQualificationsStore();
  const orgQualifications = useOrgQualificationsStore((s) => s.orgQualifications);
  const isOrgQualListLoading = useOrgQualificationsStore((s) => s.loading);
  const {
    initialized: typePresetsListInitialized,
    load: loadTypePresets,
    list: typePresetsList,
  } = useShiftTypePresetsList();

  const localizationBaseSubset = `templates.shiftTemplates.drawers`;
  const localizationSubset = `${localizationBaseSubset}.shiftTemplateDrawer.${drawerMode}`;

  const loading = entityLoading || isOrgQualListLoading || !typePresetsListInitialized || locationsLoading;

  useEffect(() => {
    if (!orgQualifications) {
      loadOrgQuals();
    }
  }, [orgQualifications]);

  useEffect(() => {
    if (!orgQualifications) {
      loadOrgQuals();
    }
  }, [orgQualifications]);

  useEffect(() => {
    loadTypePresets({
      location: {
        key: 'location',
        value: [currentLocationID],
      },
    });
  }, [loadTypePresets]);

  const methods = useForm<FormData>({
    defaultValues: {
      name: entity?.name || '',
      department: entity?.departmentNode?.id || '',
      subDepartment: entity?.subDepartmentNode?.id || '',
      specialty: entity?.specialty?.id || '',
      qualifications: entity?.qualifications?.map((q) => q.id) || [],
      recurrenceType: entity?.recurrenceType || null,
      tags: entity?.tags.length ? entity.tags.join(', ') : '',
      startTime: entity?.startTime ? dayjs(entity.startTime, 'HH:mm:ss') : null,
      endTime: entity?.startTime ? dayjs(entity?.startTime, 'HH:mm:ss').add(entity.durationInSeconds, 'seconds') : null,
      shiftTypePreset: entity?.shiftTimePresetTemplate?.id || '',
      durationInDays: entity?.durationInDays || 1, // Only for ShiftSeries and ShiftRotation
      schedule: entity?.seriesRecurrence
        ? (JSON.parse(entity.seriesRecurrence) as Record<string, Record<WeekDay, Schedule[]>>)
        : WeekVariants[currentLocation?.workWeekDayStart || 'Monday'],
      rotationSequence: entity?.rotationRecurrence ? (JSON.parse(entity.rotationRecurrence) as SequenceItem[]) : [],
    },
  });

  useEffect(() => {
    if (entity) {
      methods.reset({
        name: entity?.name || '',
        department: entity?.departmentNode?.id || '',
        subDepartment: entity?.subDepartmentNode?.id || '',
        specialty: entity?.specialty?.id || '',
        qualifications: entity?.qualifications?.map((q) => q.id) || [],
        recurrenceType: entity?.recurrenceType || 'single',
        tags: entity?.tags.length ? entity.tags.join(', ') : '',
        startTime: entity?.startTime ? dayjs(entity.startTime, 'HH:mm:ss') : null,
        endTime: entity?.startTime
          ? dayjs(entity?.startTime, 'HH:mm:ss').add(entity.durationInSeconds, 'seconds')
          : null,
        shiftTypePreset: entity?.shiftTimePresetTemplate?.id || '',
        durationInDays: entity?.durationInDays || 1, // Only for ShiftSeries and ShiftRotation
        schedule: entity?.seriesRecurrence
          ? (JSON.parse(entity.seriesRecurrence) as Record<string, Record<WeekDay, Schedule[]>>)
          : WeekVariants[currentLocation?.workWeekDayStart || 'Monday'],
        rotationSequence: entity?.rotationRecurrence ? (JSON.parse(entity.rotationRecurrence) as SequenceItem[]) : [],
      });
    }
  }, [entity]);

  const handleClose = () => {
    methods.reset({
      name: '',
      department: '',
      subDepartment: '',
      specialty: '',
      qualifications: [],
      recurrenceType: null,
      tags: '',
      startTime: null,
      endTime: null,
      shiftTypePreset: '',
      durationInDays: 0, // Only for ShiftSeries and ShiftRotation
      schedule: {},
      rotationSequence: [],
    });
    closeDrawer();
  };

  const handleSubmit = async (actionMode: 'create' | 'update', data: FormData) => {
    const dataToSend: SingleShiftTemplateCreateData = {
      name: data.name,
      departmentNodeId: data.department,
      subDepartmentNodeId: data.subDepartment,
      specialtyId: data.specialty,
      qualificationIds: data.qualifications,
      recurrenceType: data.recurrenceType as 'single' | 'series' | 'rotation',
      startTime: dayjs(data.startTime).format('HH:mm:ss'),
      shiftTimePresetTemplateId: data.shiftTypePreset,
      durationInSeconds:
        data.endTime && data.startTime
          ? dayjs(
              data.endTime.add(data.startTime.isAfter(data.endTime) || data.startTime.isSame(data.endTime) ? 1 : 0),
            ).diff(dayjs(data.startTime), 'seconds')
          : 0,
      durationInDays: data.durationInDays || 1, // Only for ShiftSeries and ShiftRotation
      tags: data.tags ? data.tags.split(',').map((tag) => tag.trim()) : [],
      rotationRecurrence: JSON.stringify(data.rotationSequence),
      seriesRecurrence: JSON.stringify(data.schedule),
      locationId: currentLocation?.id || '',
    };
    try {
      if (actionMode === 'create') {
        await create(dataToSend);
        return;
      }
      if (actionMode === 'update' && currentEntityId) {
        await update({ ...dataToSend, id: currentEntityId });
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        showErrorIntl(parseError(error, `${localizationBaseSubset}.errors`).message);
      }
      throw error;
    }
  };

  const handleNextPage = async (mode: 'create' | 'update' = 'create') => {
    try {
      await methods.handleSubmit(handleSubmit.bind(handleSubmit, mode))();
      if (Object.keys(methods.formState.errors).length) {
        // stop propagation, while form is invalid
        return;
      }
    } catch (error) {
      console.error('Error while submitting. Stop propagation', error);
      // stop propagation
      return;
    }
    onSaveCall && onSaveCall();
    handleClose();
  };

  const handleDelete = async () => {
    if (currentEntityId) {
      try {
        await deleteEntity(currentEntityId);
        onSaveCall && onSaveCall();
        handleClose();
      } catch (error) {
        if (error instanceof Error) {
          showError(error.message);
        }
      }
    }
  };

  const recurrenceType = methods.watch('recurrenceType');

  let dialogHeading = `${localizationSubset}.dialogHeading`;

  return (
    <ClosableDrawer
      open={open}
      onClose={handleClose}
      loading={loading}
      controls={
        <Stack direction={'row-reverse'} spacing={2} sx={(theme) => ({ paddingBottom: theme.spacing(1) })}>
          <MenuButton
            variant="contained"
            onClick={handleNextPage.bind(handleNextPage, drawerMode === 'edit' ? 'update' : 'create')}
            disabled={loading}
          >
            <FormattedMessage id={`${localizationSubset}.menuButtons.save.label`} />
          </MenuButton>
          {(drawerMode === 'edit' || drawerMode === 'copy') && (
            <MenuButton variant="outlined" onClick={handleNextPage.bind(handleNextPage, 'create')} disabled={loading}>
              <FormattedMessage id={`${localizationSubset}.menuButtons.duplicate.label`} />
            </MenuButton>
          )}
          {drawerMode === 'edit' && (
            <MenuButton variant="outlined" color="error" onClick={handleDelete} disabled={loading}>
              <FormattedMessage id={`${localizationSubset}.menuButtons.delete.label`} />
            </MenuButton>
          )}
          <Box flex={1} />
          <MenuButton
            onClick={handleClose}
            sx={(theme) => ({ color: theme.palette.text.secondary })}
            disabled={loading}
          >
            <FormattedMessage id={`${localizationSubset}.menuButtons.cancel.label`} />
          </MenuButton>
        </Stack>
      }
    >
      <Stack direction={'column'} sx={{ height: '100%' }} spacing={2} width={{ xs: '300px', sm: '600px' }}>
        {/* Header */}
        <Stack direction={'row'}>
          <Typography variant="h5">
            <FormattedMessage id={dialogHeading} />
          </Typography>
          <Box flex={1} />
        </Stack>
        <Form
          formUtils={methods}
          onSubmit={handleSubmit.bind(handleSubmit, drawerMode === 'edit' ? 'update' : 'create')}
        >
          {/* Template info */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.templateInfo.sectTitle`} />}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormSelect
                  label={<FormattedMessage id={`${localizationSubset}.templateInfo.recurrenceType.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.templateInfo.recurrenceType.placeholder`,
                  })}
                  fullWidth
                  required
                  name="recurrenceType"
                  options={['single', 'series', 'rotation'].map((type) => ({
                    key: type,
                    label: intl.formatMessage({
                      id: `${localizationSubset}.templateInfo.recurrenceType.options.${type.toLowerCase()}`,
                    }),
                    value: type,
                  }))}
                />
              </Grid>
              <Grid item xs={6}>
                <FormTextField
                  name="name"
                  label={<FormattedMessage id={`${localizationSubset}.templateInfo.name.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.templateInfo.name.label`,
                  })}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <QualificationSelect
                  fullWidth
                  name="qualifications"
                  required
                  localizationSubset={localizationSubset}
                  // disabledOptions={assigned.flatMap((a) => a.qualifications.map((q) => q.id))}
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </FormBlock>
          {/* Template configuration */}
          {!recurrenceType && (
            <FormBlock title={<FormattedMessage id={`${localizationSubset}.templateConfiguration.sectTitle`} />}>
              <Typography variant="body2" color={theme.palette.text.secondary}>
                <FormattedMessage id={`${localizationSubset}.templateConfiguration.notSelected`} />
              </Typography>
            </FormBlock>
          )}

          {recurrenceType === 'single' && <SingleTemplateConfiguration localizationSubset={localizationSubset} />}
          {recurrenceType === 'series' && (
            <SeriesTemplateConfiguration
              localizationSubset={localizationSubset}
              defaultSchedule={
                entity?.seriesRecurrence
                  ? (JSON.parse(entity.seriesRecurrence) as Record<WeekDay, Schedule[]>)
                  : WeekVariants[currentLocation?.workWeekDayStart || 'Monday']
              }
            />
          )}
          {recurrenceType === 'rotation' && (
            <RotationTemplateConfiguration
              localizationSubset={localizationSubset}
              defaultSequence={
                entity?.rotationRecurrence ? (JSON.parse(entity.rotationRecurrence) as SequenceItem[]) : []
              }
            />
          )}

          {/* Location */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.location.sectTitle`} />}>
            <LocationSelectionSection
              localizationSubset={localizationSubset}
              loading={loading}
              hideLocation
              required={false}
            />
          </FormBlock>

          {/* Additional info */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.additionalInfo.sectTitle`} />}>
            <Grid container spacing={2} padding={2}>
              <Grid item xs={6}>
                <FormTextField
                  disabled={loading}
                  label={<FormattedMessage id={`${localizationSubset}.additionalInfo.tag.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.additionalInfo.tag.placeholder`,
                  })}
                  fullWidth
                  name="tags"
                />
              </Grid>
            </Grid>
          </FormBlock>
        </Form>
      </Stack>
    </ClosableDrawer>
  );
}

type FormData = LocationFormData & {
  recurrenceType: 'single' | 'series' | 'rotation' | null;
  qualifications: string[];
  name: string;
  tags: string;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  shiftTypePreset: string;
  durationInDays: number; // Only for ShiftSeries and ShiftRotation
  schedule: Record<WeekDay, Schedule[]>;
  rotationSequence: SequenceItem[]; // Only for ShiftRotation
};

type SingleTemplateConfigurationProps = {
  localizationSubset: string;
};

function SingleTemplateConfiguration({ localizationSubset }: SingleTemplateConfigurationProps) {
  const intl = useIntl();
  const theme = useTheme();

  const { list: typePresetsList } = useShiftTypePresetsList();

  const { watch, setValue, getFieldState } = useFormContext<FormData>();
  const startTime = watch('startTime');
  const endTime = watch('endTime');

  const shiftTimePresetTemplateId = watch('shiftTypePreset');

  useEffect(() => {
    if (shiftTimePresetTemplateId) {
      const preset = typePresetsList.find((p) => p.id === shiftTimePresetTemplateId);
      if (preset) {
        setValue('startTime', dayjs(preset.startTime, 'HH:mm:ss'));
        setValue('endTime', dayjs(preset.startTime, 'HH:mm').add(preset.durationInSeconds, 'seconds'));
      }
    }
  }, [shiftTimePresetTemplateId, typePresetsList, setValue]);

  const localizationSubsetBase = `${localizationSubset}.templateConfiguration`;
  localizationSubset = `${localizationSubsetBase}.single`;

  return (
    <FormBlock title={<FormattedMessage id={`${localizationSubsetBase}.sectTitle`} />}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormSelect
            label={<FormattedMessage id={`${localizationSubset}.shiftPreset.label`} />}
            placeholder={intl.formatMessage({
              id: `${localizationSubset}.shiftPreset.placeholder`,
            })}
            fullWidth
            name="shiftTypePreset"
            options={typePresetsList.map((preset) => ({
              key: preset.id,
              label: preset.name,
              value: preset.id,
            }))}
          />
        </Grid>
        <Grid item xs={6}>
          <Stack direction={'column'} spacing={1} alignItems="flex-start">
            <Typography variant="subtitle2">
              <FormattedMessage id={`${localizationSubset}.duration.label`} />
            </Typography>
            <Chip
              sx={{
                width: 'auto',
              }}
              color={endTime && startTime ? 'primary' : 'default'}
              label={
                endTime && startTime
                  ? dayjs
                      .duration(
                        dayjs(endTime.add(startTime.isAfter(endTime) || startTime.isSame(endTime) ? 1 : 0)).diff(
                          dayjs(startTime),
                        ),
                        'milliseconds',
                      )
                      .humanize()
                  : intl.formatMessage({ id: `${localizationSubset}.duration.notSelected` })
              }
            />
          </Stack>
        </Grid>
        <Grid item xs={6}>
          <FormTimePicker
            name="startTime"
            label={<FormattedMessage id={`${localizationSubset}.startTime.label`} />}
            placeholder={intl.formatMessage({
              id: `${localizationSubset}.startTime.placeholder`,
            })}
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={6}>
          <FormTimePicker
            name="endTime"
            label={<FormattedMessage id={`${localizationSubset}.endTime.label`} />}
            placeholder={intl.formatMessage({
              id: `${localizationSubset}.endTime.placeholder`,
            })}
            required
            fullWidth
          />
        </Grid>
      </Grid>
    </FormBlock>
  );
}

type SeriesTemplateConfigurationProps = {
  localizationSubset: string;
  defaultSchedule: Record<WeekDay, Schedule[]>;
};

function SeriesTemplateConfiguration({ localizationSubset, defaultSchedule }: SeriesTemplateConfigurationProps) {
  const intl = useIntl();
  const theme = useTheme();

  const { list: typePresetsList } = useShiftTypePresetsList();

  const { watch, setValue, getFieldState } = useFormContext<FormData>();
  const startTime = watch('startTime');
  const endTime = watch('endTime');
  const durationInDays = watch('durationInDays');
  const scheduleList = watch('schedule');

  const shiftTimePresetTemplateId = watch('shiftTypePreset');

  const [scheduledTimeOfAWeek, setScheduledTimeOfAWeek] = useState<number>(0); // Seconds !!!

  useEffect(() => {
    if (shiftTimePresetTemplateId) {
      const preset = typePresetsList.find((p) => p.id === shiftTimePresetTemplateId);
      if (preset) {
        setValue('startTime', dayjs(preset.startTime, 'HH:mm:ss'));
        setValue('endTime', dayjs(preset.startTime, 'HH:mm').add(preset.durationInSeconds, 'seconds'));
      }
    }
  }, [shiftTimePresetTemplateId, typePresetsList, setValue]);

  useEffect(() => {
    if (durationInDays) {
      if (durationInDays < 1 || durationInDays > 365) {
        setValue('durationInDays', 1);
      }
    }
  }, [durationInDays]);

  useEffect(() => {
    const timeAtWeek: number = Object.keys(scheduleList).reduce((acc, dayName) => {
      const timeOfDay = scheduleList[dayName as WeekDay].reduce((acc, timeset) => {
        const timeOfSection = timeset.durationInSeconds;
        return acc + (timeOfSection || 0);
      }, 0);
      return acc + timeOfDay;
    }, 0);
    setScheduledTimeOfAWeek(timeAtWeek);
  }, [scheduleList]);

  const handleScheduleDayChange = (dayMark: WeekDay, dayRangeSet: TimeRangeRecord[]) => {
    scheduleList[dayMark] = dayRangeSet.reduce<Schedule[]>((acc, range) => {
      let duration = 0;
      if (range.disabled || !range.start?.isValid() || !range.end?.isValid()) {
        return acc;
      }
      if (range.start && range.end) {
        if (range.start.toDate() > range.end.toDate()) {
          duration = dayjs(range.end.add(1, 'day')).diff(range.start) / 1000;
        } else {
          duration = dayjs(range.end).diff(range.start) / 1000;
        }
      }

      acc.push({
        dayOfWeek: dayMark,
        startTime: range.start?.format('HH:mm:ss') || '00:00:00',
        durationInSeconds: duration,
      });

      return acc;
    }, []);

    setValue('schedule', scheduleList);
  };

  const localizationSubsetBase = `${localizationSubset}.templateConfiguration`;
  localizationSubset = `${localizationSubsetBase}.series`;

  return (
    <>
      <FormBlock title={<FormattedMessage id={`${localizationSubsetBase}.sectTitle`} />}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormTextField
              name="durationInDays"
              label={<FormattedMessage id={`${localizationSubset}.daysDuration.label`} />}
              placeholder={intl.formatMessage({
                id: `${localizationSubset}.daysDuration.placeholder`,
              })}
              required
              type="number"
              InputProps={{
                inputProps: {
                  min: 1,
                  max: 365,
                },
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <FormSelect
              label={<FormattedMessage id={`${localizationSubset}.shiftPreset.label`} />}
              placeholder={intl.formatMessage({
                id: `${localizationSubset}.shiftPreset.placeholder`,
              })}
              fullWidth
              name="shiftTypePreset"
              options={typePresetsList.map((preset) => ({
                key: preset.id,
                label: preset.name,
                value: preset.id,
              }))}
            />
          </Grid>
          <Grid item xs={6}>
            <FormTimePicker
              name="startTime"
              label={<FormattedMessage id={`${localizationSubset}.startTime.label`} />}
              placeholder={intl.formatMessage({
                id: `${localizationSubset}.startTime.placeholder`,
              })}
              required
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <FormTimePicker
              name="endTime"
              label={<FormattedMessage id={`${localizationSubset}.endTime.label`} />}
              placeholder={intl.formatMessage({
                id: `${localizationSubset}.endTime.placeholder`,
              })}
              required
              fullWidth
            />
          </Grid>
        </Grid>
      </FormBlock>

      <CollapsibleFormBlock
        expanded={true}
        sectTitle={
          <>
            <FormattedMessage id={`${localizationSubset}.seriesSchedule.sectTitle`} />
            <Box sx={(theme) => ({ color: theme.palette.error.main, marginLeft: '3px' })}>*</Box>
          </>
        }
        sectTitleBadges={
          <Chip
            size="small"
            label={intl.formatMessage(
              { id: `${localizationSubset}.seriesSchedule.sectBadges.spendTime` },
              { amount: dayjs.duration(scheduledTimeOfAWeek, 'seconds').humanize() },
            )}
          />
        }
      >
        <Stack direction={'column'} spacing={2}>
          {Object.keys(defaultSchedule).map((dayMark: string, idx) => {
            return (
              <DayScheduleSection
                key={idx}
                day={dayMark}
                timeSet={defaultSchedule[dayMark as WeekDay].map((s) => ({
                  start: s.startTime ? dayjs(s.startTime, 'HH:mm:ss') : null,
                  end: s.durationInSeconds ? dayjs(s.startTime, 'HH:mm:ss').add(s.durationInSeconds, 'seconds') : null,
                  disabled: !s.durationInSeconds,
                }))}
                disabled={!startTime || !endTime}
                defaultStartTime={startTime ? dayjs(startTime) : null}
                defaultEndTime={endTime ? dayjs(endTime) : null}
                localizationSubset={`${localizationSubset}.seriesSchedule.daySchedule`}
                onChange={(dayRangeSet) => {
                  handleScheduleDayChange(dayMark as WeekDay, dayRangeSet);
                }}
              />
            );
          })}
        </Stack>
      </CollapsibleFormBlock>
    </>
  );
}

type RotationTemplateConfigurationProps = {
  localizationSubset: string;
  defaultSequence: SequenceItem[];
};

function RotationTemplateConfiguration({ localizationSubset, defaultSequence }: RotationTemplateConfigurationProps) {
  const intl = useIntl();
  const theme = useTheme();

  const { list: typePresetsList } = useShiftTypePresetsList();

  const { watch, setValue, getFieldState } = useFormContext<FormData>();
  const durationInDays = watch('durationInDays');

  const shiftTimePresetTemplateId = watch('shiftTypePreset');

  const [sequence, setSequence] = useState<SequenceItem[]>([]);

  useEffect(() => {
    if (shiftTimePresetTemplateId) {
      const preset = typePresetsList.find((p) => p.id === shiftTimePresetTemplateId);
      if (preset) {
        setValue('startTime', dayjs(preset.startTime, 'HH:mm:ss'));
        setValue('endTime', dayjs(preset.startTime, 'HH:mm').add(preset.durationInSeconds, 'seconds'));
      }
    }
  }, [shiftTimePresetTemplateId, typePresetsList, setValue]);

  useEffect(() => {
    if (durationInDays) {
      if (durationInDays < 1 || durationInDays > 365) {
        setValue('durationInDays', 1);
      }
    }
  }, [durationInDays]);

  useEffect(() => {
    setValue('rotationSequence', sequence);
  }, [sequence]);

  const localizationSubsetBase = `${localizationSubset}.templateConfiguration`;
  localizationSubset = `${localizationSubsetBase}.rotation`;

  return (
    <>
      <FormBlock title={<FormattedMessage id={`${localizationSubsetBase}.sectTitle`} />}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormTextField
              name="durationInDays"
              label={<FormattedMessage id={`${localizationSubset}.daysDuration.label`} />}
              placeholder={intl.formatMessage({
                id: `${localizationSubset}.daysDuration.placeholder`,
              })}
              required
              type="number"
              InputProps={{
                inputProps: {
                  min: 1,
                  max: 365,
                },
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <FormSelect
              label={<FormattedMessage id={`${localizationSubset}.shiftPreset.label`} />}
              placeholder={intl.formatMessage({
                id: `${localizationSubset}.shiftPreset.placeholder`,
              })}
              fullWidth
              name="shiftTypePreset"
              options={typePresetsList.map((preset) => ({
                key: preset.id,
                label: preset.name,
                value: preset.id,
              }))}
            />
          </Grid>
          <Grid item xs={6}>
            <FormTimePicker
              name="startTime"
              label={<FormattedMessage id={`${localizationSubset}.startTime.label`} />}
              placeholder={intl.formatMessage({
                id: `${localizationSubset}.startTime.placeholder`,
              })}
              required
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <FormTimePicker
              name="endTime"
              label={<FormattedMessage id={`${localizationSubset}.endTime.label`} />}
              placeholder={intl.formatMessage({
                id: `${localizationSubset}.endTime.placeholder`,
              })}
              required
              fullWidth
            />
          </Grid>
        </Grid>
      </FormBlock>
      <CollapsibleFormBlock
        sectTitle={<FormattedMessage id={`${localizationSubset}.rotationSequence.sectTitle`} />}
        expanded
        sectTitleBadges={
          <Chip
            size="small"
            label={
              sequence.length
                ? sequence.reduce((acc, seq, idx, array) => {
                    if (array.length === 1 && array[0].durationInDays === 0) {
                      return intl.formatMessage({
                        id: `${localizationSubset}.rotationSequence.sectBadges.noSequence`,
                      });
                    }
                    const label = seq.isPause ? 'Off' : 'On';
                    return `${acc} ${seq.durationInDays} ${label}`;
                  }, '')
                : intl.formatMessage({ id: `${localizationSubset}.rotationSequence.sectBadges.noSequence` })
            }
          />
        }
      >
        <RotationSequenceSection
          localizationSubset={localizationSubset}
          defaultSequence={defaultSequence}
          onChange={(seq) => {
            setSequence(seq);
          }}
        />
      </CollapsibleFormBlock>
    </>
  );
}

const MenuButton = styled(Button)(({ theme }) => ({
  minWidth: theme.spacing(16.75),
}));
